import { useState } from 'react';

const FilterSection = ({ title, items, filterKey, isExpanded, onToggle, checkedItems, onCheckboxChange }) => (
    <div className="border-b border-border pb-4">
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between py-3 hover:text-[#FF6B35] transition-colors"
        >
            <span className="font-semibold text-sm">{title}</span>
            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-lg`}></i>
        </button>
        {isExpanded && (
            <div className="space-y-2 mt-2">
                {items.map((item) => (
                    <label key={item} className="flex items-center space-x-2 cursor-pointer hover:text-[#FF6B35] transition-colors">
                        <input
                            type="checkbox"
                            checked={checkedItems.includes(item)}
                            onChange={() => onCheckboxChange(filterKey, item)}
                            className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                        />
                        <span className="text-sm">{item}</span>
                    </label>
                ))}
            </div>
        )}
    </div>
);

export default function TVFilterPanel({
    filters,
    selectedFilters,
    onFilterChange,
    onClearFilters,
    productCount,
    isOpen,
    onClose
}) {
    const [expandedSections, setExpandedSections] = useState({
        brand: true,
        price: true,
        screenSize: true,
        displayType: true,
        resolution: true,
        smartTV: false,
        os: false,
        refreshRate: false,
        hdmi: false,
        availability: false,
        discount: false,
        rating: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleCheckboxChange = (filterType, value) => {
        const currentValues = selectedFilters[filterType] || [];
        const newValues = currentValues.includes(value)
            ? currentValues.filter((v) => v !== value)
            : [...currentValues, value];
        onFilterChange(filterType, newValues);
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                ></div>
            )}

            {/* Filter Panel */}
            <div
                className={`fixed lg:sticky top-0 left-0 h-screen lg:h-auto w-80 lg:w-full bg-card z-50 lg:z-0 overflow-y-auto transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                    }`}
            >
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold">Filters</h3>
                        <button
                            onClick={onClose}
                            className="lg:hidden w-8 h-8 flex items-center justify-center hover:bg-muted rounded-full transition-colors"
                        >
                            <i className="ri-close-line text-xl"></i>
                        </button>
                    </div>

                    {/* Clear All */}
                    <button
                        onClick={onClearFilters}
                        className="w-full mb-6 px-4 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm font-medium whitespace-nowrap"
                    >
                        Clear All Filters
                    </button>

                    {/* Product Count */}
                    <div className="mb-6 p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">
                            <span className="font-bold text-foreground">{productCount}</span> products found
                        </p>
                    </div>

                    {/* Filter Sections */}
                    <div className="space-y-4">
                        <FilterSection
                            title="Brand"
                            items={filters.brands}
                            filterKey="brand"
                            isExpanded={expandedSections.brand}
                            onToggle={() => toggleSection('brand')}
                            checkedItems={selectedFilters.brand || []}
                            onCheckboxChange={handleCheckboxChange}
                        />

                        {/* Price Range */}
                        <div className="border-b border-border pb-4">
                            <button
                                onClick={() => toggleSection('price')}
                                className="w-full flex items-center justify-between py-3 hover:text-[#FF6B35] transition-colors"
                            >
                                <span className="font-semibold text-sm">Price Range</span>
                                <i className={`ri-arrow-${expandedSections.price ? 'up' : 'down'}-s-line text-lg`}></i>
                            </button>
                            {expandedSections.price && (
                                <div className="mt-4 space-y-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span>KSh {selectedFilters.priceRange?.[0] || filters.priceRange[0]}</span>
                                        <span>KSh {selectedFilters.priceRange?.[1] || filters.priceRange[1]}</span>
                                    </div>
                                    <input
                                        type="range"
                                        min={filters.priceRange[0]}
                                        max={filters.priceRange[1]}
                                        value={selectedFilters.priceRange?.[1] || filters.priceRange[1]}
                                        onChange={(e) => onFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value)])}
                                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                                    />
                                </div>
                            )}
                        </div>

                        <FilterSection
                            title="Screen Size"
                            items={filters.screenSizes}
                            filterKey="screenSize"
                            isExpanded={expandedSections.screenSize}
                            onToggle={() => toggleSection('screenSize')}
                            checkedItems={selectedFilters.screenSize || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="Display Type"
                            items={filters.displayTypes}
                            filterKey="displayType"
                            isExpanded={expandedSections.displayType}
                            onToggle={() => toggleSection('displayType')}
                            checkedItems={selectedFilters.displayType || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="Resolution"
                            items={filters.resolutions}
                            filterKey="resolution"
                            isExpanded={expandedSections.resolution}
                            onToggle={() => toggleSection('resolution')}
                            checkedItems={selectedFilters.resolution || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="Smart TV"
                            items={filters.smartTV}
                            filterKey="smartTV"
                            isExpanded={expandedSections.smartTV}
                            onToggle={() => toggleSection('smartTV')}
                            checkedItems={selectedFilters.smartTV || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="Operating System"
                            items={filters.operatingSystems}
                            filterKey="os"
                            isExpanded={expandedSections.os}
                            onToggle={() => toggleSection('os')}
                            checkedItems={selectedFilters.os || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="Refresh Rate"
                            items={filters.refreshRates}
                            filterKey="refreshRate"
                            isExpanded={expandedSections.refreshRate}
                            onToggle={() => toggleSection('refreshRate')}
                            checkedItems={selectedFilters.refreshRate || []}
                            onCheckboxChange={handleCheckboxChange}
                        />
                        <FilterSection
                            title="HDMI Ports"
                            items={filters.hdmiPorts}
                            filterKey="hdmiPorts"
                            isExpanded={expandedSections.hdmi}
                            onToggle={() => toggleSection('hdmi')}
                            checkedItems={selectedFilters.hdmiPorts || []}
                            onCheckboxChange={handleCheckboxChange}
                        />

                        {/* Availability */}
                        <div className="border-b border-border pb-4">
                            <button
                                onClick={() => toggleSection('availability')}
                                className="w-full flex items-center justify-between py-3 hover:text-[#FF6B35] transition-colors"
                            >
                                <span className="font-semibold text-sm">Availability</span>
                                <i className={`ri-arrow-${expandedSections.availability ? 'up' : 'down'}-s-line text-lg`}></i>
                            </button>
                            {expandedSections.availability && (
                                <div className="space-y-2 mt-2">
                                    {filters.availability.map((item) => (
                                        <label key={item} className="flex items-center space-x-2 cursor-pointer hover:text-[#FF6B35] transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={(selectedFilters.availability || []).includes(item)}
                                                onChange={() => handleCheckboxChange('availability', item)}
                                                className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                            />
                                            <span className="text-sm">{item}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Discount */}
                        <div className="border-b border-border pb-4">
                            <button
                                onClick={() => toggleSection('discount')}
                                className="w-full flex items-center justify-between py-3 hover:text-[#FF6B35] transition-colors"
                            >
                                <span className="font-semibold text-sm">Discount</span>
                                <i className={`ri-arrow-${expandedSections.discount ? 'up' : 'down'}-s-line text-lg`}></i>
                            </button>
                            {expandedSections.discount && (
                                <div className="space-y-2 mt-2">
                                    {filters.discounts.map((item) => (
                                        <label key={item} className="flex items-center space-x-2 cursor-pointer hover:text-[#FF6B35] transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={(selectedFilters.discount || []).includes(item)}
                                                onChange={() => handleCheckboxChange('discount', item)}
                                                className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                            />
                                            <span className="text-sm">{item}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Rating */}
                        <div className="border-b border-border pb-4">
                            <button
                                onClick={() => toggleSection('rating')}
                                className="w-full flex items-center justify-between py-3 hover:text-[#FF6B35] transition-colors"
                            >
                                <span className="font-semibold text-sm">Rating</span>
                                <i className={`ri-arrow-${expandedSections.rating ? 'up' : 'down'}-s-line text-lg`}></i>
                            </button>
                            {expandedSections.rating && (
                                <div className="space-y-2 mt-2">
                                    {filters.ratings.map((rating) => (
                                        <label key={rating} className="flex items-center space-x-2 cursor-pointer hover:text-[#FF6B35] transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={(selectedFilters.rating || []).includes(rating)}
                                                onChange={() => handleCheckboxChange('rating', rating)}
                                                className="w-4 h-4 rounded border-border text-[#FF6B35] focus:ring-[#FF6B35] cursor-pointer"
                                            />
                                            <span className="text-sm flex items-center">
                                                {rating}
                                                <i className="ri-star-fill text-yellow-400 ml-1 text-xs w-4 h-4 flex items-center justify-center"></i>
                                                & above
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
